import { getDependencies } from "@/libs/cv/modules/illumination/dependencies"
import type { ProgressListener } from "@/libs/request/fetch-progress"
import { throttleMark, type PerformanceEntry } from "@/libs/telemetry/performance"
import { getFetchEntriesMeasurements } from "@/libs/telemetry/performance/fetch-entries"
import type { Graph } from "../../framework"
import type { FramePixels, Landmarks } from "../types.d"
import type { Dependencies, Output as Illumination, Input } from "./illumination.model"
import {
  EVENT_TYPE,
  MESSAGE_TYPE,
  type DataEvent,
  type InitEvent,
  type Message,
  type ResultMessage,
} from "./illumination.worker"
import IlluminationWorker from "./illumination.worker.ts?worker&inline"

export type { Illumination }

export type CreateOptions = {
  performance?: PerformanceEntry
  initialState?: Partial<Illumination>
  onProgress?: ProgressListener
}

export const defaultIllumination: Illumination = {
  intensity: 0,
  azimuth: 0,
  elevation: 0,
}

export const create = async <I extends unknown[], O extends unknown[]>(
  graph: Graph<I, O>,
  opts?: CreateOptions,
) => {
  /**
   * Internal node‘s state.
   * In case there is no face detection, the result of the last light intensity calculation will be returned.
   */
  const nodeState: { isReady: boolean; illumination: Illumination } = {
    isReady: false,
    illumination: Object.assign(defaultIllumination, opts?.initialState),
  }
  const loadingPerf = opts?.performance?.mark("cv:onnx-loading")

  const predict = await createPredict({
    onProgress: opts?.onProgress,
    onReady: async (deps) => {
      const fetchMeasurements = await getFetchEntriesMeasurements((entry) =>
        deps.some((asset) => entry.name.includes(asset.url)),
      )

      loadingPerf?.measure({ children: fetchMeasurements })
      nodeState.isReady = true
    },
  })

  const inferenceMark = throttleMark(opts?.performance, 1000 / 2)

  return graph.node<
    [frame: FramePixels, faceMesh: { landmarks: Landmarks } | null],
    [Illumination]
  >({
    process: async (ctx) => {
      const frame = ctx.in(0)
      const faceMesh = ctx.in(1)
      // Skip detached frames
      if (nodeState.isReady && faceMesh && frame.data.byteLength > 0) {
        const inferencePerf = inferenceMark("cv:illumination-inference")
        nodeState.illumination = await predict(frame, faceMesh.landmarks)
        inferencePerf?.measure()
      }

      ctx.out(0, nodeState.illumination)
    },
  })
}

const getROI = (landmarks: Landmarks) => {
  const top = landmarks[10].y
  const bottom = landmarks[0].y
  const left = Math.min(landmarks[127].x, landmarks[24].x, landmarks[143].x)
  const right = Math.max(landmarks[264].x, landmarks[356].x, landmarks[372].x)

  return [top, left, 0, bottom, right, 1]
}

const frameToInput = (frame: FramePixels, landmarks: Landmarks): Input => ({
  frame,
  roi: getROI(landmarks),
})

const createPredict = async ({
  onProgress,
  onReady,
}: {
  onProgress?: ProgressListener
  onReady: (deps: Dependencies) => void
}) => {
  /**
   * A workaround to make worker work
   * Vite incorrectly imports dependencies urls in getDependencies function in worker environment imported as &inline
   * It builds URL using self.location.href as base, and inside worker it's equal to `blob:...`, which makes it invalid
   * So we need to import dependencies before worker creation and pass them as a message
   */
  const deps = await getDependencies()

  try {
    const worker = new IlluminationWorker()

    // Wait for worker to be ready
    await new Promise<void>((resolve) => {
      worker.onmessage = (e: MessageEvent<Message>) => {
        if (e.data.type === MESSAGE_TYPE.PROGRESS) onProgress?.(e.data.progress)
        else if (e.data.type === MESSAGE_TYPE.READY) resolve()
      }
      worker.postMessage({ type: EVENT_TYPE.INIT, dependencies: deps } satisfies InitEvent)
    })

    const operations = new Map<number, (data: ResultMessage["result"]) => void>()

    worker.onmessage = async (e: MessageEvent<Message>) => {
      if (e.data.type === MESSAGE_TYPE.RESULT) {
        operations.get(e.data.timestamp)?.(e.data.result)
        operations.delete(e.data.timestamp)
      }
    }

    return (frame: FramePixels, landmarks: Landmarks) =>
      new Promise<Illumination>((resolve, reject) => {
        const timestamp = Date.now()

        operations.set(timestamp, resolve)

        worker.onerror = (e) => reject(e)
        worker.postMessage(
          {
            type: EVENT_TYPE.DATA,
            ...frameToInput(frame, landmarks),
            timestamp,
          } satisfies DataEvent,
          [frame.data.buffer],
        )
      })
  } catch (e) {
    // Raises when website's CSP disallows blob: protocol, trying to recover
    if ((e as Error)?.name === "SecurityError") {
      console.warn(
        "[LIQA] Warning: Content Security Policy disallows blob: protocol for WebWorkers. For effective LIQA work, please follow the instructions on https://docs.liqa.haut.ai/going-live.html#set-up-csp-for-using-liqa. For any questions, contact the Haut.AI support team (https://docs.liqa.haut.ai/support-desk.html#reaching-for-the-support-team).",
      )
    }

    const model = await import("./illumination.model").then(({ create }) =>
      create(deps, { onProgress }),
    )

    return (frame: FramePixels, landmarks: Landmarks) =>
      model.predict(frameToInput(frame, landmarks))
  } finally {
    onReady(deps)
  }
}
